// Variant-level stock helpers — single source of truth for how the storefront
// reads a product's inventory.
//
// inventory shape: [{ width: Number, colour_code: String, quantity: Number }]
// Only POPULAR fabric colours are tracked per (width × colour). A tracked
// combo with quantity > 0 is in stock; quantity 0 or no entry is out of stock.
// Non-popular colours aren't tracked at all — they're made-to-order.

// Quantity for a specific (width, colour code). Returns null when the combo
// isn't tracked (no matching entry).
export function variantQuantity(product, width, colourCode) {
  const inv = product && product.inventory;
  if (!Array.isArray(inv) || width == null || width === '' || !colourCode) return null;
  const w = Number(width);
  const entry = inv.find((e) => Number(e.width) === w && e.colour_code === colourCode);
  return entry ? (Number(entry.quantity) || 0) : null;
}

// Is a colour variant flagged popular (i.e. stock-tracked)?
export function isColourPopular(product, colourCode) {
  if (!colourCode) return false;
  const cv = (product && product.color_variants || []).find((c) => c.code === colourCode);
  return !!(cv && cv.popular);
}

// Stock state for a selected (width × colour) variant:
//   'in-stock'      popular colour, quantity > 0
//   'out-of-stock'  popular colour, quantity 0 or untracked
//   'made-to-order' non-popular colour (never stock-tracked)
//   'unknown'       width or colour not chosen yet
export function variantStockState(product, width, colourCode) {
  if (width == null || width === '' || !colourCode) return 'unknown';
  if (!isColourPopular(product, colourCode)) return 'made-to-order';
  const qty = variantQuantity(product, width, colourCode);
  return qty && qty > 0 ? 'in-stock' : 'out-of-stock';
}

// Does the product have ANY in-stock variant? Drives the homepage "In Stock"
// badge and the product-level availability signal.
export function productHasStock(product) {
  const inv = product && product.inventory;
  if (!Array.isArray(inv)) return false;
  return inv.some((e) => Number(e.quantity) > 0);
}

// In-stock combinations grouped by width for an "Available Now" overview.
// Returns [{ width, colours: [colourName, ...] }] sorted by width, colours in
// the product's own colour order. Derived straight from `inventory`, so it
// always matches what the admin set — no separate list to drift out of sync.
export function availableVariants(product) {
  const inv = product && product.inventory;
  if (!Array.isArray(inv)) return [];
  const variants = (product && product.color_variants) || [];
  const codeToName = new Map(variants.map((c) => [c.code, c.name || c.code]));
  const codeOrder = new Map(variants.map((c, i) => [c.code, i]));

  const byWidth = new Map();
  for (const e of inv) {
    if (!(Number(e.quantity) > 0)) continue;
    const w = Number(e.width);
    if (!byWidth.has(w)) byWidth.set(w, []);
    byWidth.get(w).push(e.colour_code);
  }
  return [...byWidth.entries()]
    .sort((a, b) => a[0] - b[0])
    .map(([width, codes]) => ({
      width,
      colours: codes
        .sort((a, b) => (codeOrder.get(a) ?? 0) - (codeOrder.get(b) ?? 0))
        .map((code) => codeToName.get(code) || code),
    }));
}

// NOTE: lead-time phrases now live in config/leadTimes.js (awningLeadTime),
// the single source of truth. This module stays focused on reading inventory;
// callers combine variantStockState() below with awningLeadTime() to decide
// what to show. (The old width-based leadTimeWeeks() helper was removed.)

// Set of widths that have at least one in-stock colour (for selector hints).
export function inStockWidths(product) {
  const inv = product && product.inventory;
  const set = new Set();
  if (Array.isArray(inv)) {
    for (const e of inv) if (Number(e.quantity) > 0) set.add(Number(e.width));
  }
  return set;
}
